if (!customElements.get('custom-slideshow-abbyson')) {
  class SlideshowComponent extends HTMLElement {
    constructor() {
      super();
      this.effect = this.dataset.effect || 'slide';
      this.autoplay = this.dataset.autoplay === 'true';
      this.speed = parseInt(this.dataset.speed, 10) || 5000;

      this.currentIndex = 0;
      this.trackPosition = 0;
      this.usesClones = false;
      this.pendingSnapTo = null;
      this.timer = null;

      // drag state
      this.isDragging = false;
      this.dragStartX = 0;
      this.dragDeltaX = 0;
      this.suppressNextClick = false;

      this.onPrev = this.onPrev.bind(this);
      this.onNext = this.onNext.bind(this);
      this.onDotClick = this.onDotClick.bind(this);
      this.onVisibilityChange = this.onVisibilityChange.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onTrackClickCapture = this.onTrackClickCapture.bind(this);
      this.onTrackTransitionEnd = this.onTrackTransitionEnd.bind(this);
    }

    connectedCallback() {
      this.track = this.querySelector('.slideshow__track');
      if (!this.track) return;

      this.realSlides = Array.from(this.track.children).filter((el) =>
        el.classList.contains('slideshow__slide')
      );
      if (this.realSlides.length === 0) return;

      this.dots = Array.from(this.querySelectorAll('[data-slideshow-dot]'));
      this.prevBtn = this.querySelector('[data-slideshow-prev]');
      this.nextBtn = this.querySelector('[data-slideshow-next]');

      // For the "Slide" (horizontal) effect with more than one slide, build
      // an infinite loop: clone the first slide and append it at the end,
      // clone the last slide and prepend it at the start. This lets the
      // track always keep translating in the SAME direction on next/prev,
      // instead of visually reversing when it wraps from last -> first.
      if (this.effect === 'slide' && this.realSlides.length > 1 && !this.track.dataset.loopReady) {
        this.setupInfiniteClones();
        this.track.dataset.loopReady = 'true';
      } else if (this.track.dataset.loopReady === 'true') {
        this.usesClones = true;
        this.trackPosition = 1;
      }

      if (this.prevBtn) this.prevBtn.addEventListener('click', this.onPrev);
      if (this.nextBtn) this.nextBtn.addEventListener('click', this.onNext);
      this.dots.forEach((dot) => dot.addEventListener('click', this.onDotClick));

      this.addEventListener('mouseenter', () => this.pause());
      this.addEventListener('mouseleave', () => this.play());
      this.addEventListener('focusin', () => this.pause());
      this.addEventListener('focusout', () => this.play());
      this.addEventListener('keydown', this.onKeydown);

      if (this.realSlides.length > 1) {
        this.track.addEventListener('pointerdown', this.onPointerDown);
        this.track.addEventListener('click', this.onTrackClickCapture, true);
      }

      this.track.addEventListener('transitionend', this.onTrackTransitionEnd);
      document.addEventListener('visibilitychange', this.onVisibilityChange);

      this.jumpTo(0, false);

      if (this.realSlides.length > 1) {
        this.play();
      }

      document.addEventListener('shopify:section:select', (e) => {
        if (e.target.contains(this)) this.pause();
      });
      document.addEventListener('shopify:block:select', (event) => {
        const slide = event.target.closest('.slideshow__slide');
        if (slide) {
          const idx = this.realSlides.indexOf(slide);
          if (idx > -1) this.jumpTo(idx, false);
          this.pause();
        }
      });
      document.addEventListener('shopify:block:deselect', () => {
        this.play();
      });
    }

    disconnectedCallback() {
      this.pause();
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      window.removeEventListener('pointermove', this.onPointerMove);
      window.removeEventListener('pointerup', this.onPointerUp);
    }

    /* ---------------- Infinite-loop clone setup ---------------- */

    setupInfiniteClones() {
      const first = this.realSlides[0];
      const last = this.realSlides[this.realSlides.length - 1];

      this.cloneFirst = first.cloneNode(true);
      this.cloneLast = last.cloneNode(true);

      [this.cloneFirst, this.cloneLast].forEach((clone) => {
        clone.classList.remove('is-active');
        clone.setAttribute('aria-hidden', 'true');
        clone.removeAttribute('id');
        clone.querySelectorAll('a, button').forEach((el) => el.setAttribute('tabindex', '-1'));
      });

      this.track.appendChild(this.cloneFirst);
      this.track.insertBefore(this.cloneLast, this.track.firstChild);

      this.usesClones = true;
      this.trackPosition = 1;
    }

    onTrackTransitionEnd(e) {
      if (e.target !== this.track || e.propertyName !== 'transform') return;
      if (this.pendingSnapTo != null) {
        this.trackPosition = this.pendingSnapTo;
        this.pendingSnapTo = null;
        this.setTrackTransform(this.trackPosition, false);
      }
    }

    /* ---------------- Core navigation ---------------- */

    updateActiveClasses(realIndex) {
      this.realSlides.forEach((slide, i) => {
        slide.classList.toggle('is-active', i === realIndex);
        slide.setAttribute('aria-hidden', i === realIndex ? 'false' : 'true');
      });
      this.dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === realIndex);
        dot.setAttribute('aria-selected', i === realIndex ? 'true' : 'false');
      });
      this.currentIndex = realIndex;
    }

    setTrackTransform(position, animate) {
      if (this.effect !== 'slide' || !this.track) return;
      this.track.style.transition = animate ? '' : 'none';
      this.track.style.transform = `translateX(-${position * 100}%)`;
      if (!animate) {
        // force reflow so the next transform change animates
        void this.track.offsetHeight;
        this.track.style.transition = '';
      }
    }

    /** Direct, non-directional jump (used by dots, keyboard-select in the
     *  theme editor, and initial setup). No infinite-loop bookkeeping. */
    jumpTo(index, animate = true) {
      const total = this.realSlides.length;
      const idx = ((index % total) + total) % total;
      this.updateActiveClasses(idx);
      if (this.effect === 'slide') {
        this.trackPosition = this.usesClones ? idx + 1 : idx;
        this.setTrackTransform(this.trackPosition, animate);
      }
    }

    /** Directional step forward. Always visually moves the same way. */
    advanceNext() {
      const total = this.realSlides.length;
      if (total < 2) return;
      const newReal = (this.currentIndex + 1) % total;

      if (this.effect === 'slide' && this.usesClones) {
        this.trackPosition += 1;
        this.updateActiveClasses(newReal);
        this.setTrackTransform(this.trackPosition, true);
        if (this.trackPosition === total + 1) {
          this.pendingSnapTo = 1;
        }
      } else {
        this.jumpTo(newReal, true);
      }
    }

    /** Directional step backward. Always visually moves the same way. */
    advancePrev() {
      const total = this.realSlides.length;
      if (total < 2) return;
      const newReal = (this.currentIndex - 1 + total) % total;

      if (this.effect === 'slide' && this.usesClones) {
        this.trackPosition -= 1;
        this.updateActiveClasses(newReal);
        this.setTrackTransform(this.trackPosition, true);
        if (this.trackPosition === 0) {
          this.pendingSnapTo = total;
        }
      } else {
        this.jumpTo(newReal, true);
      }
    }

    onKeydown(e) {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.onPrev();
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.onNext();
      }
    }

    onVisibilityChange() {
      if (document.hidden) {
        this.pause();
      } else {
        this.play();
      }
    }

    onPrev() {
      this.advancePrev();
      this.restart();
    }

    onNext() {
      this.advanceNext();
      this.restart();
    }

    onDotClick(e) {
      const index = parseInt(e.currentTarget.dataset.index, 10);
      this.jumpTo(index, true);
      this.restart();
    }

    /* ---------------- Drag / swipe (mouse + touch via Pointer Events) ---------------- */

    onPointerDown(e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      if (this.realSlides.length < 2) return;

      this.isDragging = true;
      this.dragStartX = e.clientX;
      this.dragDeltaX = 0;
      this.pause();

      if (this.effect === 'slide') {
        this.track.classList.add('is-dragging');
      }

      window.addEventListener('pointermove', this.onPointerMove);
      window.addEventListener('pointerup', this.onPointerUp);
      window.addEventListener('pointercancel', this.onPointerUp);

      this.focus({ preventScroll: true });
    }

    onPointerMove(e) {
      if (!this.isDragging) return;
      this.dragDeltaX = e.clientX - this.dragStartX;

      if (Math.abs(this.dragDeltaX) > 8) {
        this.suppressNextClick = true;
      }

      if (this.effect === 'slide' && this.track) {
        const width = this.track.offsetWidth || 1;
        const percent = (this.dragDeltaX / width) * 100;
        const base = this.usesClones ? this.trackPosition : this.currentIndex;
        this.track.style.transition = 'none';
        this.track.style.transform = `translateX(calc(-${base * 100}% + ${percent}%))`;
      }
    }

    onPointerUp() {
      if (!this.isDragging) return;
      this.isDragging = false;

      window.removeEventListener('pointermove', this.onPointerMove);
      window.removeEventListener('pointerup', this.onPointerUp);
      window.removeEventListener('pointercancel', this.onPointerUp);

      if (this.effect === 'slide') {
        this.track.classList.remove('is-dragging');
      }

      const width = (this.track && this.track.offsetWidth) || 1;
      const threshold = Math.max(40, width * 0.12);

      if (this.dragDeltaX <= -threshold) {
        this.advanceNext();
      } else if (this.dragDeltaX >= threshold) {
        this.advancePrev();
      } else if (this.effect === 'slide') {
        const base = this.usesClones ? this.trackPosition : this.currentIndex;
        this.setTrackTransform(base, true);
      }

      this.dragDeltaX = 0;
      this.restart();

      setTimeout(() => {
        this.suppressNextClick = false;
      }, 0);
    }

    onTrackClickCapture(e) {
      if (this.suppressNextClick) {
        e.preventDefault();
        e.stopPropagation();
      }
    }

    /* ---------------- Autoplay ---------------- */

    play() {
      if (!this.autoplay || this.realSlides.length < 2) return;
      this.pause();
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reducedMotion) return;
      this.timer = setInterval(() => {
        this.advanceNext();
      }, this.speed);
    }

    pause() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    }

    restart() {
      if (this.autoplay) {
        this.pause();
        this.play();
      }
    }
  }

  customElements.define('custom-slideshow-abbyson', SlideshowComponent);
}